"use client"

import styled from "@emotion/styled"
import {
  Int32StringValueTuple,
  LostFoundColorPublicResponse,
  LostFoundContentPublicResponse,
} from "api"
import { useRouter, useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import CommonTabs from "../Common/CommonTabs"
import Result from "./components/Result"
import InfoTab from "./InfoTab"
import LossReports from "./LossRepots"
import Form from "./MainForm"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 48px;
  gap: 32px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding-bottom: 64px;
    gap: 48px;
  }
`

export type FormError = "notSubmitted" | "yes" | "no"

export default function ContactContent({
  tabsData,
  contactTypesData,
  locale,
  lostFoundContentData,
  lostFoundColorsData,
  lostAndFoundStatus,
  tooltipText = "",
}: {
  tabsData: {
    contactUs: {
      data: any
      tabTitle: any
    }
    inquiries: {
      data: any
      tabTitle: any
    }
    lost: {
      data: any
      tabTitle: any
    }
  }
  contactTypesData: Int32StringValueTuple[]
  locale: "he" | "en" | "ar" | "ru"
  lostFoundContentData: LostFoundContentPublicResponse[]
  lostFoundColorsData: LostFoundColorPublicResponse[]
  lostAndFoundStatus: any
  tooltipText?: string
}) {
  const [isFormError, setIsFormError] = useState<"notSubmitted" | "yes" | "no">(
    "notSubmitted",
  )
  const router = useRouter()
  const layoutRef = useRef<HTMLDivElement>(null)
  const searchParams = useSearchParams()
  const formParam = searchParams.get("form")
  const resetParam = searchParams.get("reset")
  const [value, setValue] = useState(0)
  const [id, setId] = useState<string>()
  const [selectedFormType, setSelectedFormType] = useState<number | null>(
    formParam ? Number(formParam) : null,
  )

  // Handle reset parameter
  useEffect(() => {
    if (resetParam === "true") {
      setIsFormError("notSubmitted")
      setValue(0)
      // Remove the reset parameter from URL
      const url = new URL(window.location.href)
      url.searchParams.delete("reset")
      router.replace(url.pathname + url.search)
    }
  }, [resetParam, router])

  const handleResult = (type: FormError, id?: string) => {
    setIsFormError(type)
    setId(id)
  }

  const handleLostStatus = () => {
    setIsFormError("notSubmitted")
    setValue(2)
  }

  useEffect(() => {
    if (formParam) {
      setValue(1)
      setSelectedFormType(Number(formParam))
      layoutRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [formParam])

  useEffect(() => {
    if (isFormError !== "notSubmitted" && layoutRef.current) {
      layoutRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [isFormError])

  const tabs = [
    {
      label: tabsData.contactUs.tabTitle,
      content: <InfoTab data={tabsData.contactUs.data} />,
    },
    {
      label: tabsData.inquiries.tabTitle,
      content: (
        <Form
          contactTypesData={contactTypesData}
          handleResult={handleResult}
          locale={locale}
          initialFormType={selectedFormType}
          lostFoundContentData={lostFoundContentData}
          lostFoundColorsData={lostFoundColorsData}
          lostAndFoundStatus={lostAndFoundStatus}
          contactUsData={tabsData.contactUs.data}
          tooltipText={tooltipText}
        />
      ),
    },
    {
      label: tabsData.lost.tabTitle,
      content: <LossReports data={tabsData.lost.data} locale={locale} />,
    },
  ]

  return (
    <StyledLayout ref={layoutRef}>
      {isFormError === "notSubmitted" ? (
        <CommonTabs
          data={tabs.filter((tab) => tab.label)}
          value={value}
          setValue={setValue}
          autoFocus={!!formParam}
        />
      ) : (
        <Result isFormError={isFormError} id={id} />
      )}
    </StyledLayout>
  )
}
